import {ComponentProps, useCallback, useState} from "react";
import styled from "styled-components/macro";
import {BoxCard, BoxCardContent} from "../components/BoxCard";
import logo from "../assets/logo.png";
import loginBackground from "../assets/login-background.jpg";
import {SERVICE_HOST} from "../services";
import {serialize as cookieSerialize} from "cookie";
import {HeadingCard} from "../components/HeadingCard";
import {Button} from "../components/Button";
import {BoxRow, BoxRowCenter} from "../components/BoxRow";
import {MostlyFluidLayoutApart, MostlyFluidLayoutRight,} from "../components/MostlyFluidLayout";
import {BoxBlockCenter} from "../components/BoxBlock";
import {MostlyFluidSpacedVertical16px} from "../components/MostlyFluidSpaced";
import {BoxMarginTop16px} from "../components/BoxMargin";
import {Toast} from "../components/Toast";
import {TextBaseLight} from "../components/Text";
import {FieldText} from "../components/FieldText";
import {FieldSelect} from "../components/FieldSelect";

const Page = styled.div`
    box-sizing: border-box;
    min-width: 100vw;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url("${loginBackground}");
    background-size: cover;
    padding: 32px;
`;

const LoginContainer = styled.div`
    flex: 0 1 500px;
`;

const Logo = styled.img`
    width: 32px;
`;

export const Login = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [country, setCountry] = useState<string | undefined>("DK");
    const [isDisabled, setIsDisabled] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const onSubmit: ComponentProps<"form">["onSubmit"] = useCallback(
        async (e) => {
            if (isDisabled) {
                return;
            }

            setIsDisabled(true);
            setError(undefined);

            fetch(`${SERVICE_HOST}/login`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    country,
                    username,
                    password,
                }),
                credentials: "include",
            })
                .then(({status}) => {
                    if (status >= 200 && status <= 204) {
                        const regionCookie = cookieSerialize("region-cookie", country!, {
                            path: "/",
                        });
                        document.cookie = regionCookie;
                        window.location.reload();
                    } else if (status >= 400 && status <= 401) {
                        setError("Incorrect username or password");
                    } else if (status === 503) {
                        setError("Login is unavailable. Try again later");
                    } else {
                        setError("Something is wrong. Try again later");
                    }
                })
                .catch(() => setError("Something went wrong - try again later"))
                .finally(() => setIsDisabled(false));

            e.preventDefault();
            return false;
        },
        [isDisabled, password, country, username]
    );

    return (
        <Page>
            <LoginContainer>
                <MostlyFluidSpacedVertical16px>
                    {error !== undefined && (
                        <Toast type="element" color="error">
                            <TextBaseLight>
                                <strong>Error:</strong> {error}
                            </TextBaseLight>
                        </Toast>
                    )}

                    <BoxRow>
                        <BoxCard>
                            <form onSubmit={onSubmit}>
                                <BoxCardContent>
                                    <BoxRowCenter>
                                        <MostlyFluidLayoutApart>
                                            <BoxBlockCenter>
                                                <HeadingCard tag="h3">Hørebil</HeadingCard>
                                            </BoxBlockCenter>
                                            <Logo src={logo} alt="Logo"/>
                                        </MostlyFluidLayoutApart>
                                    </BoxRowCenter>
                                </BoxCardContent>

                                <BoxCardContent>
                                    <MostlyFluidSpacedVertical16px>
                                        <FieldSelect
                                            disabled={isDisabled}
                                            label="Region"
                                            placeholder="Select region"
                                            options={[{country: "DK", title: "Danmark"}].map(({country, title}) => ({
                                                label: title,
                                                value: country,
                                            }))}
                                            onChange={setCountry}
                                            value={country}
                                        />
                                        <FieldText
                                            disabled={isDisabled}
                                            type="text"
                                            label="Username"
                                            placeholder="Enter username"
                                            onChange={setUsername}
                                            value={username}
                                            autoComplete="username"
                                        />
                                        <FieldText
                                            disabled={isDisabled}
                                            type="password"
                                            label="Password"
                                            placeholder="Enter password"
                                            onChange={setPassword}
                                            value={password}
                                            autoComplete="current-password"
                                        />
                                        <BoxMarginTop16px>
                                            <BoxRow>
                                                <MostlyFluidLayoutRight>
                                                    <Button disabled={isDisabled} type="submit">
                                                        Log in
                                                    </Button>
                                                </MostlyFluidLayoutRight>
                                            </BoxRow>
                                        </BoxMarginTop16px>
                                    </MostlyFluidSpacedVertical16px>
                                </BoxCardContent>
                            </form>
                        </BoxCard>
                    </BoxRow>
                </MostlyFluidSpacedVertical16px>
            </LoginContainer>
        </Page>
    );
};